package com.essntl.uikit.components.expandable

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.animateContentSize
import androidx.compose.animation.core.Animatable
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.RoundRect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.onPlaced
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.layout.positionInParent
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.toSize
import com.essntl.uikit.components.iconbutton.flat.EsMediumFlatIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlinx.coroutines.launch

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun ExpandableTabs(
    id: String,
    tabList: List<String>,
    modifier: Modifier = Modifier,
    expandable: Boolean = true,
    tabContent: @Composable ColumnScope.(index: Int) -> Unit,
) {
    var selectedTab by remember { mutableStateOf(tabList.firstOrNull().orEmpty()) }
    var isDetailsExpanded by remember { mutableStateOf(true) }

    val density = LocalDensity.current
    val scope = rememberCoroutineScope()
    val pagerState = rememberPagerState(
        pageCount = { tabList.size },
    )

    val currentTabPosition = remember {
        Animatable(0f)
    }

    val currentTabWidth = remember {
        Animatable(0f)
    }

    val tabPosition = remember {
        mutableStateMapOf<Int, Offset>()
    }

    val tabSize = remember {
        mutableStateMapOf<Int, Size>()
    }

    LaunchedEffect(pagerState.currentPage) {
        tabList.getOrNull(pagerState.currentPage)?.let {
            selectedTab = it
        }
    }

    LaunchedEffect(id) {
        if (pagerState.currentPage != 0)
            pagerState.animateScrollToPage(0)
    }

    LaunchedEffect(tabPosition.toMap(), tabSize.toMap(), pagerState.targetPage, id) {
        val targetTabPosition = tabPosition[pagerState.targetPage] ?: return@LaunchedEffect
        val targetTabSize = tabSize[pagerState.targetPage] ?: return@LaunchedEffect

        if (currentTabWidth.value == 0f || !pagerState.isScrollInProgress) {
            currentTabPosition.snapTo(targetTabPosition.x)
            currentTabWidth.snapTo(targetTabSize.width)
        } else {
            launch {
                currentTabPosition.animateTo(
                    targetValue = targetTabPosition.x,
                )
            }

            launch {
                currentTabWidth.animateTo(
                    targetValue = targetTabSize.width,
                )
            }
        }
    }

    Column(
        verticalArrangement = Arrangement.Top,
        horizontalAlignment = Alignment.Start,
        modifier = modifier
            .background(
                color = MaterialTheme.esColorScheme.bgSecondary,
                shape = RoundedCornerShape(EsConstants.SectionBorderRadius),
            )
            .padding(vertical = 1.5.spacing)
            .padding(horizontal = 2.5.spacing),
    ) {
        var tabRowWidth by remember {
            mutableStateOf(0.dp)
        }

        Row(
            modifier = Modifier.fillMaxWidth(),
        ) {
            val lazyListState = rememberLazyListState()
            var tabRowHeight by remember {
                mutableStateOf(0.dp)
            }

            Box(
                modifier = Modifier
                    .onSizeChanged {
                        val width = with(density) { it.width.toDp() }
                        val height = with(density) { it.height.toDp() }

                        if (tabRowWidth != width)
                            tabRowWidth = width

                        if (tabRowHeight != height)
                            tabRowHeight = height
                    }
                    .weight(1f)
                    .clip(RoundedCornerShape(1.spacing)),
            ) {
                LazyRow(
                    state = lazyListState,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    items(tabList) { tab ->
                        val isSelected by remember(tab, selectedTab) {
                            mutableStateOf(tab == selectedTab)
                        }

                        Text(
                            text = tab,
                            color =
                                if (isSelected)
                                    MaterialTheme.esColorScheme.textAccent
                                else
                                    MaterialTheme.esColorScheme.textPrimary,
                            style = MaterialTheme.esTypography.p1Regular16,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier
                                .onPlaced {
                                    tabPosition[tabList.indexOf(tab)] = it.positionInParent()
                                }
                                .onSizeChanged {
                                    tabSize[tabList.indexOf(tab)] = it.toSize()
                                }
                                .clip(RoundedCornerShape(2.spacing))
                                .clickable {
                                    scope.launch {
                                        val index = tabList.indexOf(tab)
                                        if (index != -1)
                                            pagerState.animateScrollToPage(tabList.indexOf(tab))
                                    }
                                }
                                .padding(1.spacing),
                        )
                    }
                }

                Box(
                    modifier = Modifier
                        .graphicsLayer {
                            alpha =
                                if (lazyListState.canScrollBackward)
                                    1f
                                else
                                    0f
                        }
                        .align(Alignment.CenterStart)
                        .width(10.dp)
                        .height(tabRowHeight)
                        .background(
                            brush = Brush.horizontalGradient(
                                colors = listOf(
                                    MaterialTheme.esColorScheme.primary.copy(alpha = .2f),
                                    Color.Transparent,
                                ),
                            ),
                        ),
                )

                Box(
                    modifier = Modifier
                        .graphicsLayer {
                            alpha =
                                if (lazyListState.canScrollForward)
                                    1f
                                else
                                    0f
                        }
                        .align(Alignment.CenterEnd)
                        .width(10.dp)
                        .height(tabRowHeight)
                        .background(
                            brush = Brush.horizontalGradient(
                                colors = listOf(
                                    Color.Transparent,
                                    MaterialTheme.esColorScheme.primary.copy(alpha = .2f),
                                ),
                            ),
                        ),
                )
            }

            if (expandable)
                EsMediumFlatIconButton(
                    onClick = {
                        isDetailsExpanded = !isDetailsExpanded
                    },
                    iconRes = if (isDetailsExpanded) EsIcons.ArrowUp else EsIcons.ArrowDown,
                )
        }

        val esColorScheme = MaterialTheme.esColorScheme

        Canvas(
            modifier = Modifier
                .width(tabRowWidth)
                .height(4.dp)
                .clipToBounds(),
        ) {
            val cornerRadius = CornerRadius(10f, 10f)
            val path = Path().apply {
                addRoundRect(
                    RoundRect(
                        rect = Rect(
                            offset = Offset(
                                x = currentTabPosition.value,
                                y = 0f,
                            ),
                            size = Size(currentTabWidth.value, size.height),
                        ),
                        topLeft = cornerRadius,
                        topRight = cornerRadius,
                    ),
                )
            }
            drawPath(
                path = path,
                color = esColorScheme.textAccent,
            )
        }

        HorizontalDivider(
            modifier = Modifier
                .alpha(.4f),
        )

        AnimatedVisibility(
            visible = isDetailsExpanded,
            modifier = Modifier
                .fillMaxWidth(),
//                .weight(1f)
        ) {
            HorizontalPager(
                state = pagerState,
                userScrollEnabled = false,
                modifier = Modifier
                    .animateContentSize(),
//                    .fillMaxSize()
            ) { index ->
                val tab = remember(index, tabList) {
                    tabList.getOrNull(index)
                }

                if (tab != null)
                    key("$tab-$index") {
                        tabContent(index)
                    }
            }
        }
    }
}